import { computeGaps, type WindowLike } from './conflicts';
import { staffs, type ConfirmationStatus, type ShiftState } from './constants';
import { dayExtent } from './time';

export interface CoverageShift extends WindowLike {
  state: ShiftState;
  volunteerId: string | null;
}

export type PostDayStatus = 'none' | 'gap' | 'part' | 'ok';

/**
 * Whole-day coverage status of one post — the map's pin colour:
 *  - `none` — nothing planned that day (no shifts)
 *  - `gap`  — ≥ 15 min of the operating window uncovered
 *  - `part` — covered, but someone still hasn't confirmed (or has rejected)
 *  - `ok`   — covered and everyone confirmed
 * Coverage counts every staffed shift regardless of confirmation status
 * — a rejection is a blocker, not a hole; the hole appears
 * only once the coordinator actually unassigns. Open slots never cover.
 * With no operating windows the shift extent is the basis instead.
 *
 * Confirmation is asked of each shift (SPEC §1.1F), not of its holder: somebody
 * who agreed to this post and has yet to answer another leaves this one `ok`.
 *
 * `external` rides alongside the status rather than inside it (SPEC §1.1J): an
 * external team is a provenance, not a state of confirmation, so a post where a
 * waiting volunteer works beside an outside group still has to read `part`.
 * External slots are outside the confirmation lifecycle — nobody ever asks them
 * — so they are skipped by the `pending` test, and a post covered only by a
 * group reads `ok` instead of sitting yellow for the whole season.
 */
export function postDayStatus<S extends CoverageShift>(
  shifts: S[],
  windows: WindowLike[],
  statusOfShift: (shift: S) => ConfirmationStatus | undefined,
  isExternal: (id: string) => boolean = () => false,
): { status: PostDayStatus; gapMin: number; external: boolean } {
  if (!shifts.length) return { status: 'none', gapMin: 0, external: false };

  const staffed = shifts.filter(staffs);
  const basis = windows.length ? windows : [dayExtent(shifts)!];
  const gapMin = computeGaps(basis, staffed).reduce((a, g) => a + (g.toMin - g.fromMin), 0);
  const external = staffed.some((s) => isExternal(s.volunteerId!));

  if (!staffed.length) return { status: 'gap', gapMin, external };
  if (gapMin >= 15) return { status: 'gap', gapMin, external };
  const pending = staffed.some((s) => {
    if (s.state === 'done') return false;
    if (isExternal(s.volunteerId!)) return false;
    const st = statusOfShift(s);
    return st !== undefined && st !== 'potrjen';
  });
  return { status: pending ? 'part' : 'ok', gapMin, external };
}
